import { noop } from "lodash";
import { useCallback, useEffect, useRef } from "react";

export function useTimeout(callback: () => void, delay: number | null) {
  const savedCallback = useRef<typeof callback>(noop);
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const clear = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (delay === null) return;
    clear();
    timeoutId.current = setTimeout(() => {
      savedCallback.current();
      timeoutId.current = null;
    }, delay);
  }, [delay, clear]);

  const reset = useCallback(() => {
    start();
  }, [start]);

  // Auto-start if delay is provided
  useEffect(() => {
    if (!delay && delay !== 0) {
      return;
    }
    start();
    return clear; // cleanup on unmount or delay change
  }, [delay, start, clear]);

  return { start, clear, reset };
}
